import * as yup from "yup";
import { streetAddressLineOneValidationSchema, streetAddressLineTwoValidationSchema, cityValidationSchema, stateValidationSchema } from "../../../../AddressFields";

/**
 * Shared Validation Schema for Personal Information
 * 
 * A comprehensive Yup validation schema that defines validation rules for personal
 * information collection in the GEAO onboarding process. This schema handles
 * validation for personal details, address information, business locations,
 * and lines of business with conditional validation logic.
 * 
 * @type {yup.ObjectSchema} Yup object schema containing validation rules
 * 
 * Key validation features:
 * - Personal information: Date of birth, SSN with confirmation matching (conditionally required when not offshore)
 * - Address validation: Complete residential address with conditional Florida county selection
 * - Business information: Florida business location with conditional county requirements
 * - Lines of business: At least one selection required with conditional "other" specification
 * - Work arrangement: Required selection
 * - Conditional validations based on state selection and business location choices
 * - Offshore: Country field required when offshore, zip code format varies by country
 * 
 * Conditional validation logic:
 * - SSN fields required only when isOffshoreResident is false
 * - Florida county selection required only when residential state is "FL"
 * - Business county selection required only when conducting business in Florida
 * - "Other" line of business specification required when "Other" checkbox is selected
 * - Lines of business validation triggers when no options are selected
 * - Zip code: 5 digits for US and Mexico, 6 digits for India
 * 
 * Data transformations:
 * - County fields: Converts arrays to strings for proper validation
 * - Business counties: Joins multiple selections into comma-separated string
 * 
 * @example
 * // Usage with react-hook-form
 * const methods = useForm({
 *   resolver: yupResolver(sharedValidationSchema)
 * });
 */
const sharedValidationSchema = yup.object({
    isOffshoreResident: yup.bool().optional(),
    dateOfBirth: yup.date()
        .required("Please enter your date of birth.")
        .typeError("Invalid date format.\nPlease check your entry and try again.")
        .test("age-range", "You must be between 18 and 120 years old.", function (dateOfBirth) {
            if (!dateOfBirth) {
                return true;
            }

            const today = new Date();
            const originalValue = this.originalValue;
            const [birthYear, birthMonth, birthDay] = typeof originalValue === "string" && /^\d{4}-\d{2}-\d{2}/.test(originalValue)
                ? originalValue.slice(0, 10).split("-").map(Number)
                : [dateOfBirth.getFullYear(), dateOfBirth.getMonth() + 1, dateOfBirth.getDate()];
            let age = today.getFullYear() - birthYear;
            if (today.getMonth() + 1 < birthMonth ||
                (today.getMonth() + 1 === birthMonth && today.getDate() < birthDay)) {
                age--;
            }

            return age >= 18 && age <= 120;
        }),
    ssn: yup.string()
        .when("isOffshoreResident", {
            is: true,
            then: schema => schema.nullable().optional(),
            otherwise: schema => schema.required("Please enter your SSN.").min(9, "Please enter a valid SSN.")
        }),
    ssnConfirm: yup.string()
        .when("isOffshoreResident", {
            is: true,
            then: schema => schema.nullable().optional(),
            otherwise: schema => schema.required("Please confirm your SSN.")
                .test("match", "SSN does not match", function (value) {
                    return value === this.parent.ssn;
                })
        }),
    timeAtAgency: yup.string().matches(/^\d{1,2}$/, "Enter a number").required("Enter a number"),
    soldLinesOfBusiness: yup.string()
        .when(["lobCommercial", "lobHealth", "lobHome", "lobPersonal", "lobOther"], {
            is: (lobCommercial, lobHealth, lobHome, lobPersonal, lobOther) =>
                [lobCommercial, lobHealth, lobHome, lobPersonal, lobOther].every(lob => !lob),
            then: schema => schema.required("Please select current lines of business being sold")
        }),
    lobOther: yup.bool().optional(),
    otherLineOfBusiness: yup.string()
        .when("lobOther", {
            is: true,
            then: schema => schema
                .required("Please enter other Line of Business")
        }),
    workArrangement: yup.string().required("Please select current work arrangement"),
    residentialAddress: yup.object().shape({
        country: yup.string().nullable().optional(),
        streetAddressLineOne: streetAddressLineOneValidationSchema,
        streetAddressLineTwo: streetAddressLineTwoValidationSchema,
        zipCode: yup.string()
            .when("country", (countryArr, schema) => {
                const country = Array.isArray(countryArr) ? countryArr[0] : countryArr;
                if (!country || country === "US" || country === "MX") {
                    return schema
                        .matches(/^\d{5}$/, !country || country === "US" ? "Zip code must be 5 digits." : "Zip code is invalid")
                        .required("Please enter a ZIP code.");
                }
                if (country === "IN") {
                    return schema
                        .matches(/^\d{6}$/, "Zip code is invalid")
                        .required("Please enter a ZIP code.");
                }
                return schema
                    .test("unsupported-country", "Selected country is not supported.", () => false);
            }),
        city: cityValidationSchema,
        state: stateValidationSchema,
        county: yup.string()
            .transform((value, originalValue) => {
                // If it's an array, take the first value
                if (Array.isArray(originalValue)) {
                    return originalValue[0] || "";
                }
                // If it's a string, return as is
                return originalValue;
            })
            .when("state", {
                is: state => state === "FL",
                then: schema => schema
                    .required("Please select a County"),
            })
    }).when("isOffshoreResident", {
        is: isOffshoreResident => isOffshoreResident === true || isOffshoreResident === "true",
        then: schema => schema.shape({
            country: yup.string().required("Please select a country.")
        })
    }),
    businessInFlorida: yup.bool().required("Required."),
    businessInFloridaCounty: yup.string()
        .transform((value, originalValue) => {
            // If it's an array, join it into a comma-separated string
            if (Array.isArray(originalValue)) {
                return originalValue.join(", ");
            }
            // If it's a string, return as is
            return originalValue;
        })
        .when("businessInFlorida", {
            is: true,
            then: schema => schema
                .required("Please enter FL County of operation"),
        })
});

export default sharedValidationSchema;
